import React, { useState, useRef, useCallback, useEffect, CSSProperties } from 'react';
import { useWindowSize } from '../core';

interface ResizablePanelProps {
  isExtreme?: boolean;
  minWidth?: number;
  maxWidth?: number;
  initialWidth: number;
  onResize?: (newWidth: number) => void;
  borderColor?: string;
  children?: React.ReactNode;
  sx?: CSSProperties;
}

export const ResizablePanel: React.FC<ResizablePanelProps> = ({ isExtreme, initialWidth, minWidth, maxWidth, onResize, borderColor, children, sx }) => {
  const screenSize = useWindowSize();

  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const startResize = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    startX.current = e.clientX;
    if (panelRef.current) {
      startWidth.current = panelRef.current.offsetWidth;
    }
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing && panelRef.current) {
      const deltaX = e.clientX - startX.current;
      let newWidth = Math.max(50, startWidth.current + ((isExtreme ? 1 : -1) * deltaX));

      if (newWidth > (maxWidth ?? screenSize.width / 2)) {
        newWidth = (maxWidth ?? screenSize.width / 2);
      }
      if (newWidth < (minWidth ?? 35)) {
        newWidth = (minWidth ?? 35);
      }

      setWidth(newWidth);
      if (onResize) onResize(newWidth);
    }
  }, [isResizing, onResize]);

  const stopResize = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', stopResize);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopResize);
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing, onMouseMove, stopResize]);

  return (
    <div
      ref={panelRef}
      style={{
        padding: '5px',
        ...sx,
        width: `${width}px`,
        height: '100%',
        backgroundColor: '#f0f0f0',
        position: 'relative',
      }}
    >
      {children}

      <div
        onMouseDown={startResize}
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          [isExtreme ? 'right' : 'left']: 0,
          width: '0.1em',
          cursor: 'ew-resize',
          backgroundColor: borderColor,
        }}
      />
    </div>
  );
};